/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import CheckoutTemplate from "@/abilities/shop/features/shop-light/components/checkout/templates/checkout-template.vue";
import SummaryTemplate from "../features/shop-light/components/checkout/templates/summary-template.vue";
import MailStatusTemplate from "../features/shop-light/components/checkout/templates/mail-status-template.vue";
import { CheckoutStore } from "@/abilities/shop/features/shop-light/store/checkout.store";
import { ShopCore } from "./shop-core";
export namespace ShopLight {
    export interface AdditionalArticleInfo {
        id: string;
        articleNumber: string;
        description: string;
        imageUrl: string;
        assemblyId?: string;
        price: {
            amount?: number;
            currency: string;
        };
        orderable: number;
    }

    export interface CartItem {
        cartItemId: {
            articleId: string;
            partLocation?: {
                mechanicId: string;
            };
        };
        remark?: string;
        quantity: number;
    }

    export type Article = CartItem & Omit<AdditionalArticleInfo, "id"> & { id?: string };

    export interface Config {
        id: string;
        shopServiceUrl: string;
        shopEMail: string;
    }

    /**
     * Represents a log entry from a job execution
     */
    export interface JobLogEntry {
        message: string[];
        timestamp: string;
        levelDescription: string;
    }

    /**
     * Flattened job information with execution details and logs
     */
    export interface JobInfo {
        // Job identification
        id: string;

        // Execution status
        status: string;
        attempt: number;

        // Flat array of logs
        logs: JobLogEntry[];
    }

    /**
     * Represents the complete job pipeline status information with flattened structure
     */
    export interface MailJobPipelineStatus {
        jobId: string;
        // Pipeline-level information
        status: string;
        displayName: string | null;

        // Timing information
        startDate: string | null;
        endDate: string | null;

        // Flattened job information with logs
        jobs: JobInfo[];

        // Optional dependency graph
        dependencyGraph?: string | null;
    }

    /**
     * Represents a GraphQL error from the sendMails mutation
     */
    export interface GraphQLError {
        message: string;
        path?: string[];
        extensions?: {
            code?: string;
            [key: string]: any;
        };
    }

    /**
     * Represents an error that occurred during the sendMails mutation
     * (before job creation, e.g., rate limit errors)
     */
    export interface SendMailError {
        status: "error";
        errorType: "graphql_error" | "rate_limit" | "no_job_id" | "unknown";
        message: string;
        graphqlErrors?: GraphQLError[];
    }

    /**
     * Represents an error result from the mail sending process
     * (after job creation, e.g., job failed or not found)
     */
    export interface MailJobPipelineError {
        status: "error";
        errorType:
            | "not_found"
            | "timeout"
            | "failed"
            | "fetch_error"
            | "job_failed"
            | "job_warning"
            | "unknown"
            | string;
        message: string;
        details?: MailJobPipelineStatus | null;
    }

    /**
     * Combined result type for mail sending operations
     */
    export type MailJobResult = MailJobPipelineStatus | MailJobPipelineError;

    export interface IActions extends ShopCore.IShopActions {
        // Core functionality
        addPartToShopBasket(
            articleId: string,
            amountSelected: number,
            additionalInfo?: { assemblyId?: string; mechanicId?: string }
        ): Promise<void>;
        // Basket functionality
        getBasketCount(): Promise<number>;
        upsertArticle(article: CartItem): Promise<CartItem>;
        deleteArticle(article: CartItem): Promise<boolean>;
        emptyBasket(): Promise<void>;
        sendCartRequestEmail(): Promise<string>;
    }

    export namespace Checkout {
        export enum TabState {
            INIT = "init",
            VALID = "valid",
            INVALID = "invalid",
            ERROR = "error",
            DISABLED = "disabled",
        }

        export enum TabType {
            INPUT,
            SUMMARY,
        }

        export interface TabSynchronisation {
            source: string;
            active?: () => boolean;
        }

        export interface TabContent extends Tab {
            component: TabComponent;
            props?: Record<string, any>;
            tabType?: TabType;
            isOptional?: boolean;
            syncsWith?: TabSynchronisation;
            tabPage?: TabPage;
            validator?: TabValidator;
        }

        export type TabPage = {
            tabAreas: Array<TabArea>;
        };

        export type TabArea = {
            inputs: Array<Array<TabInput>>;
            title: string;
            subtitle?: string;
        };

        export type TabInput = {
            id: string;
            label?: string;
            required?: boolean;
            hint?: string;
            type?: "text" | "dropdown" | "checkbox";
            maxLength?: number;
            rows?: number;
            options?: Array<{ label: string; value: string }>; // dropdown
            validationType?: keyof typeof inputValidators;
            inputTrigger?: (params: { value: boolean }) => void;
            checkboxModelValue?: () => boolean;
        };

        export type TabComponent = typeof CheckoutTemplate | typeof SummaryTemplate | typeof MailStatusTemplate;

        export type TabValidator = ({ allTabs }: TabValidatorOptions) => boolean;

        export interface TabValidatorOptions {
            allTabs?: TabContent[];
            state?: CheckoutStore;
            inputs?: Array<Array<TabInput>>;
        }

        export interface Tab {
            name: TabName; // Restrict name to keys of TabNames
            state?: TabState;
        }

        export interface BasketRequestInfos {
            contactData: ContactData;
            requestDetails: RequestDetails;
            billingAddress: Address;
            shippingAddress: Address;
        }

        export interface ContactData {
            additionalEmail: string;
            contactEmail: string;
            firstName: string;
            familyName: string;
            position: string;
            company: string;
        }

        export interface RequestDetails {
            subject: string;
            remark: string;
            priority: string;
        }

        interface Address {
            company: string;
            forAttentionOf: string;
            street: string;
            houseNumber: string;
            addressSupplement: string;
            city: string;
            postalCode: string;
            country: string;
        }

        const inputValidators = {
            email: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
            emailMulti: (value: string) => {
                if (!value.trim()) return true;
                const emails = value.split(",").map((e) => e.trim());
                if (emails.length > 3) return false;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emails.every((email) => emailRegex.test(email));
            },

            number: (value: string) => /^-?\d+(\.\d+)?$/.test(value),
            text: (value: string) => value.length > 0,
        };

        export function isFieldValid(field: TabInput, value: string | number) {
            if (field.validationType) {
                const isValid = inputValidators[field.validationType](String(value));
                if (!isValid) {
                    return false;
                }
            }
            if (field.required && value === "") return false;
            return true;
        }

        export type TabName =
            | "contact details"
            | "request data"
            | "billing address"
            | "shipping address"
            | "summary"
            | "confirmation";

        export const BasketRequestInfoPaths = {
            contactData: {
                contactEmail: "contactData.contactEmail",
                additionalEmail: "contactData.additionalEmail",
                firstName: "contactData.firstName",
                familyName: "contactData.familyName",
                position: "contactData.position",
                company: "contactData.company",
            },
            requestDetails: {
                subject: "requestDetails.subject",
                remark: "requestDetails.remark",
                priority: "requestDetails.priority",
            },
            billingAddress: {
                company: "billingAddress.company",
                forAttentionOf: "billingAddress.forAttentionOf",
                street: "billingAddress.street",
                houseNumber: "billingAddress.houseNumber",
                addressSupplement: "billingAddress.addressSupplement",
                postalCode: "billingAddress.postalCode",
                city: "billingAddress.city",
                country: "billingAddress.country",
                sync: "billingAddress.sync",
            },
            shippingAddress: {
                company: "shippingAddress.company",
                forAttentionOf: "shippingAddress.forAttentionOf",
                street: "shippingAddress.street",
                houseNumber: "shippingAddress.houseNumber",
                addressSupplement: "shippingAddress.addressSupplement",
                postalCode: "shippingAddress.postalCode",
                city: "shippingAddress.city",
                country: "shippingAddress.country",
            },
        };
    }
}
